import React, { useState, useEffect, useRef } from "react"
import { Bar, Line } from "react-chartjs-2"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js"
import {
  Modal, Button, DatePicker, Spin, message,
} from "antd"
import { PDFDownloadLink } from "@react-pdf/renderer"
import html2canvas from "html2canvas"
import moment from "moment"
import commonStrings from "../../commonStrings"
import CommonConstants from "../../CommonConstants"
import {
  getAllSortedIndicators,
  getMergedDataWithKeysforNew,
  getMergedDataWithKeysforOld,
  getTenureInfo,
} from "../../utilities"
import star29 from "../../../assets/images/Star29.png"
import IndicatorReportPdf from "./IndicatorReportPdf"
import pdfSvg from "../../../assets/images/pdf-file.svg"
import dottedLoader from "../../../assets/images/dottedloader.gif"

const { RangePicker } = DatePicker

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
)

const IndicatorChart = ({
  rubric,
  scatterLoading,
  graphData,
  tableListing,
  tableListingold,
  currentSchool,
  stage,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false)

  const [tenurePeriod, setTenurePeriod] = useState({
    start: null,
    end: null,
  })

  const [dateRange, setDateRange] = useState(() => {
    if (tenurePeriod.start && tenurePeriod.end) {
      return [moment(tenurePeriod.start), moment(tenurePeriod.end)]
    }
    if (rubric && rubric.tenure_start && rubric.tenure_end) {
      return [moment(rubric.tenure_start), moment(rubric.tenure_end)]
    }
    return null
  })
  const [datesCleared, setDatesCleared] = useState(false)

  const [lineChartLoading, setLineChartLoading] = useState(false)
  const [selectedIndicator, setSelectedIndicator] = useState(null)
  const [noLineDataFound, setNoLineDataFound] = useState(false)
  const [isCapturing, setIsCapturing] = useState(false)
  const [showPreviousRatingColumn, setShowPreviousRatingColumn] = useState(false)
  const [showStatusColumn, setStatusColumn] = useState(false)
  const [capturedImages, setCapturedImages] = useState({
    lineChart: null,
    barChart: null,
  })
  const [showInitialValueColumn, setShowInitialValueColumn] = useState(false)
  const [apiResponseReceived, setApiResponseReceived] = useState(false)
  const [isPdfModalVisible, setIsPdfModalVisible] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [pdfDownloadReady, setPdfDownloadReady] = useState(false)

  const [lineChartData, setLineChartData] = useState({
    labels: CommonConstants.labels,
    datasets: [
      {
        label: "Rating",
        data: [],
        borderColor: "rgb(12, 164, 165)",
        borderWidth: 2,
        fill: false,
        pointBackgroundColor: "rgb(12, 164, 165)",
        pointBorderColor: "rgb(12, 164, 165)",
        pointRadius: 4,
        pointHoverRadius: 5,
        tension: 0.1,
      },
    ],
  })

  useEffect(() => {
    if (rubric && rubric.created_at) {
      const rubricDate = new Date(rubric.created_at)
      const today = new Date()

      const currentTenureYear = getTenureInfo(today)
      const isCurrentTenure = rubricDate >= currentTenureYear.start
        && rubricDate <= currentTenureYear.end
      setShowInitialValueColumn(!isCurrentTenure) // For past tenures
      setStatusColumn(isCurrentTenure) // For current tenure

      setShowPreviousRatingColumn(isCurrentTenure) // For current tenure
    }
  }, [rubric])

  const frameImagePlugin = React.useMemo(() => {
    const starImg = new Image()
    starImg.src = star29

    return {
      id: "star29",
      afterDatasetsDraw(chart) {
        const { ctx, data } = chart
        ctx.save()

        const goalDataset = data.datasets[2]
        const currentDataset = data.datasets[1]
        if (!goalDataset || !currentDataset) return

        const goalMeta = chart.getDatasetMeta(2)
        const barMeta = chart.getDatasetMeta(1)
        if (!goalMeta || goalMeta.hidden || !barMeta) return

        goalDataset.data.forEach((goalValue, index) => {
          const currentValue = currentDataset.data[index]
          if (
            goalValue === null
            || currentValue === null
            || goalValue !== currentValue
          ) return

          const goalBar = goalMeta.data[index]
          const currentBar = barMeta.data[index]
          if (!goalBar || !currentBar) return

          const barX = goalBar.x
          const yTop = Math.min(goalBar.y, currentBar.base)
          const yBottom = Math.max(goalBar.y, currentBar.base)
          const barHeight = yBottom - yTop
          const iconSize = 14

          const numberOfIcons = goalValue === 0.5 ? 5 : goalValue === 1 ? 10 : 20
          const verticalSpacing = (barHeight - iconSize) / (numberOfIcons - 1)
          const maxHorizontalOffset = 9

          for (let i = 0; i < numberOfIcons; i += 1) {
            const xOffset = i % 2 === 0 ? -maxHorizontalOffset : maxHorizontalOffset
            const yPos = yTop + i * verticalSpacing
            ctx.drawImage(
              starImg,
              barX + xOffset - iconSize / 2,
              yPos,
              iconSize,
              iconSize,
            )
          }
        })
        ctx.restore()
      },
    }
  }, [])

  useEffect(() => {
    ChartJS.register(frameImagePlugin)
    return () => {
      ChartJS.unregister(frameImagePlugin)
    }
  }, [frameImagePlugin])

  useEffect(() => {
    if (!rubric || !rubric.id) return

    if (datesCleared) return

    if (!dateRange && (!rubric.tenure_start || !rubric.tenure_end)) {
      APIClient.get(
        `/implementation_rubrics/${rubric.id}/tenure_indicators`,
        (response) => {
          console.log("tenure_indicators response", response)
          setTenurePeriod({
            start: response.tenure_start,
            end: response.tenure_end,
          })

          setDateRange([
            moment(response.tenure_start),
            moment(response.tenure_end),
          ])
        },
      )
      return
    }
    if (!dateRange || !dateRange[0] || !dateRange[1]) return

    setApiResponseReceived(false)
    setLineChartLoading(true)
    setNoLineDataFound(false)

    function getDynamicGMTOffset() {
      const date = new Date()
      const offsetInMinutes = date.getTimezoneOffset()
      const sign = offsetInMinutes > 0 ? "-" : "+"
      const absOffset = Math.abs(offsetInMinutes)
      const hours = String(Math.floor(absOffset / 60)).padStart(2, "0")
      const minutes = String(absOffset % 60).padStart(2, "0")
      return `${sign}${hours}:${minutes}`
    }

    const gmtOffset = getDynamicGMTOffset()

    function convertToLocalTime(utcDateStr, gmtOffset) {
      const utcDate = new Date(utcDateStr)
      const [sign, hours, minutes] = gmtOffset
        .match(/([+-])(\d{2}):(\d{2})/)
        .slice(1)
      const offsetMinutes = (parseInt(hours, 10) * 60 + parseInt(minutes, 10))
        * (sign === "+" ? 1 : -1)
      const localDate = new Date(utcDate.getTime() + offsetMinutes * 60000)
      return localDate.toLocaleString()
    }

    APIClient.get(
      `/track_indicators/${rubric.id}?start_date=${dateRange[0].format(
        "YYYY-MM-DD",
      )}&end_date=${dateRange[1].format("YYYY-MM-DD")}`,
      (response) => {
        setApiResponseReceived(true)
        const filteredData = response
          .filter((item) => item.index === Number(selectedIndicator))
          .map((item) => ({
            ...item,
            created_at_local: convertToLocalTime(item.created_at, gmtOffset),
            updated_at_local: convertToLocalTime(item.updated_at, gmtOffset),
            rating: item.rating === null ? 0 : item.rating,
          }))

        if (filteredData.length === 0) {
          setNoLineDataFound(true)
          setLineChartData((prev) => ({
            ...prev,
            datasets: [
              {
                ...prev.datasets[0],
                data: [],
              },
            ],
          }))
          setLineChartLoading(false)
          return
        }

        const monthlyData = {}
        filteredData.forEach((item) => {
          const date = new Date(item.created_at_local)
          const monthIndex = date.getUTCMonth()
          const monthName = date.toLocaleString("default", {
            month: "long",
            timeZone: "UTC",
          })
          const monthKey = `${date.getUTCFullYear()}-${monthIndex}`
          if (
            !monthlyData[monthKey]
            || date > new Date(monthlyData[monthKey].created_at_local)
          ) {
            monthlyData[monthKey] = {
              rating: item.rating,
              created_at: item.created_at_local,
              monthName,
            }
          }
        })

        const allMonths = CommonConstants.labels
        const ratingsData = allMonths.map((month) => {
          const monthKey = Object.keys(monthlyData).find(
            (key) => monthlyData[key].monthName === month,
          )
          return monthKey ? monthlyData[monthKey].rating : null
        })

        setLineChartData({
          labels: allMonths,
          datasets: [
            {
              label: "Rating",
              data: ratingsData,
              borderColor: "rgb(12, 164, 165)",
              borderWidth: 2,
              fill: false,
              pointBackgroundColor: "rgb(12, 164, 165)",
              pointBorderColor: "rgb(12, 164, 165)",
              pointRadius: 4,
              pointHoverRadius: 5,
            },
          ],
        })
        setLineChartLoading(false)
      },
    )
  }, [dateRange, rubric.id, selectedIndicator, tenurePeriod])

  const allIndicators = getAllSortedIndicators(graphData)

  const barData = {
    labels: allIndicators,
    datasets: [
      {
        label: showInitialValueColumn ? "Initial Value" : "Previous Value",
        data: allIndicators.map((indicator) => {
          const item = graphData.find((el) => el.indicator === indicator)
          const value = showInitialValueColumn
            ? item
              ? item.initial_rating
              : null
            : item
              ? item.previous_rating
              : null
          return value === null ? 0.5 : value === 0 ? 0.5 : value
        }),
        backgroundColor: "#0CA4A5",
        borderColor: "#0CA4A5",
        borderWidth: 1,
      },
      {
        label: "Current Value",
        data: allIndicators.map((indicator) => {
          const item = graphData.find((el) => el.indicator === indicator)
          const value = item ? item.rating : null
          return value === null ? 0.5 : value === 0 ? 0.5 : value
        }),
        backgroundColor: "#075A5B",
        borderColor: "#075A5B",
        borderWidth: 1,
      },
      {
        label: "Goal",
        data: allIndicators.map((indicator) => {
          const item = graphData.find((el) => el.indicator === indicator)
          const value = item && item.goal_value
          return value === null ? null : value === 0 ? 0.5 : value
        }),
        backgroundColor: "#B4E3E3",
        borderColor: "#B4E3E3",
        borderWidth: 1,
      },
    ],
  }
  const barOptions = {
    responsive: true,
    onHover: (event, element) => {
      event.native.target.style.cursor = element[0] ? "pointer" : "default"
    },
    plugins: {
      legend: {
        position: "top",
        align: "center",
        labels: {
          font: { size: 14, weight: "500" },
          color: "#333333",
          usePointStyle: true,
          boxWidth: 15,
        },
      },
      tooltip: {
        callbacks: {
          title(context) {
            const indicatorNumber = barData.labels[context[0].dataIndex]
            return `Indicator ${indicatorNumber}`
          },
          label(context) {
            const label = context.dataset.label || ""
            if (context.parsed.y !== null) {
              const indicator = allIndicators[context.dataIndex]
              const item = graphData.find((el) => el.indicator === indicator)

              let actualValue
              if (context.datasetIndex === 0) {
                actualValue = showInitialValueColumn
                  ? item
                    ? item.initial_rating
                    : null
                  : item
                    ? item.previous_rating
                    : null
              } else if (context.datasetIndex === 1) {
                actualValue = item ? item.rating : null
              } else if (context.datasetIndex === 2) {
                actualValue = item ? item.goal_value : null
              }

              // Handle null values
              if (actualValue === null) {
                return `${label}: 0/null`
              }

              if (
                context.datasetIndex === 2
                && item
                && item.goal_value !== null
                && item.rating !== null
                && item.goal_value === item.rating
              ) {
                return `${label}: ${actualValue} (Goal Achieved)`
              }
              return `${label}: ${actualValue === 0 ? "0/null" : actualValue}`
            }
            return label
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Indicators",
          font: { size: 24, weight: "600" },
        },
      },
      y: {
        min: 0,
        max: 4.2,
        title: {
          display: true,
          text: "Ratings",
          font: { size: 24, weight: "600" },
        },
        ticks: {
          stepSize: 0.5,
          callback: (value) => {
            if (value === 0.5) return "0/null"
            if (Number.isInteger(value) && value !== 0) return value
            return null
          },
        },
      },
    },
    onClick: (event, elements) => {
      if (elements && elements.length > 0) {
        const clickedElement = elements[0]
        const indicatorIndex = clickedElement.index
        setSelectedIndicator(allIndicators[indicatorIndex])
        setIsModalVisible(true)
        setDateRange(
          tenurePeriod.start && tenurePeriod.end
            ? [moment(tenurePeriod.start), moment(tenurePeriod.end)]
            : rubric && rubric.tenure_start && rubric.tenure_end
              ? [moment(rubric.tenure_start), moment(rubric.tenure_end)]
              : null,
        )
        setDatesCleared(false)
      }
    },
  }

  const lineOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
        labels: {
          usePointStyle: true,
          boxWidth: 15,
        },
      },
    },
    elements: {
      line: {
        tension: 0.4,
      },
    },
    scales: {
      y: {
        min: 0,
        max: 4,
        ticks: {
          stepSize: 1,
          callback: (value) => {
            if (value === 0) return "0/null"
            return Number.isInteger(value) ? value : null
          },
        },
      },
    },
    spanGaps: true,
    datasets: {
      line: {
        spanGaps: true,
      },
    },
  }

  const handleDateChange = (dates) => {
    if (!dates || !dates[0] || !dates[1]) {
      // When clearing, set dateRange to null and reset the chart
      // setDateRange(null);
      // setDatesCleared(true);
      setDateRange(null)
      setDatesCleared(true)
      setLineChartData({
        labels: CommonConstants.labels,
        datasets: [
          {
            label: "Rating",
            data: [],
            borderColor: "rgb(12, 164, 165)",
            borderWidth: 2,
            fill: false,
            pointBackgroundColor: "rgb(12, 164, 165)",
            pointBorderColor: "rgb(12, 164, 165)",
            pointRadius: 4,
            pointHoverRadius: 5,
            tension: 0.1,
          },
        ],
      })
      setNoLineDataFound(false)
      return
    }
    setDateRange(dates)
    setDatesCleared(false)
  }

  const handleModalClose = () => {
    setLineChartData({
      labels: CommonConstants.labels,
      datasets: [
        {
          label: "Rating",
          data: [],
          borderColor: "rgb(12, 164, 165)",
          borderWidth: 2,
          fill: false,
          pointBackgroundColor: "rgb(12, 164, 165)",
          pointBorderColor: "rgb(12, 164, 165)",
          pointRadius: 4,
          pointHoverRadius: 5,
          tension: 0.1,
        },
      ],
    })
    setNoLineDataFound(false)
    setIsModalVisible(false)
    setSelectedIndicator(null)
    setApiResponseReceived(false)
    setCapturedImages({ lineChart: null, barChart: null })
  }
  const lineChartRef = useRef(null)
  const barChartRef = useRef(null)

  const captureCharts = async () => {
    try {
      setIsCapturing(true)
      await new Promise((resolve) => setTimeout(resolve, 500))

      const lineCanvas = await html2canvas(lineChartRef.current, {
        scale: 2,
        logging: false,
        useCORS: true,
        onclone: (clonedDoc) => {
          const chartElement = clonedDoc.getElementById("line-chart-container")
          if (chartElement) {
            chartElement.style.visibility = "visible"
          }
        },
      })

      const barCanvas = await html2canvas(barChartRef.current, {
        scale: 2,
        logging: false,
        useCORS: true,
        onclone: (clonedDoc) => {
          const chartElement = clonedDoc.getElementById("bar-chart-container")
          if (chartElement) {
            chartElement.style.visibility = "visible"
          }
        },
      })

      setCapturedImages({
        lineChart: lineCanvas.toDataURL("image/png"),
        barChart: barCanvas.toDataURL("image/png"),
      })
    } catch (error) {
      console.error("Error capturing charts:", error)
      throw error
    } finally {
      setIsCapturing(false)
    }
  }

  const handlePdfDownloadProcess = () => {
    if (!apiResponseReceived) {
      message.warning("Please wait for data to load before downloading PDF")
      return
    }
    setIsPdfModalVisible(true)
    setPdfLoading(true)
    setPdfDownloadReady(false)

    captureCharts().then(() => {
      setPdfLoading(false)
      setPdfDownloadReady(true)
    })
  }

  const currentschoolLogo = currentSchool && currentSchool.logo && currentSchool.logo.url
    ? currentSchool.logo.url
    : null

  const disabledDates = (current) => {
    if (!current) return false

    const currentMoment = moment.isMoment(current) ? current : moment(current)

    const tenureStart = tenurePeriod.start
      ? moment(tenurePeriod.start).startOf("day")
      : rubric && rubric.tenure_start
        ? moment(rubric.tenure_start).startOf("day")
        : null

    const tenureEnd = tenurePeriod.end
      ? moment(tenurePeriod.end).endOf("day")
      : rubric && rubric.tenure_end
        ? moment(rubric.tenure_end).endOf("day")
        : null

    if (!tenureStart || !tenureEnd) return false

    return (
      currentMoment.isBefore(tenureStart) || currentMoment.isAfter(tenureEnd)
    )
  }
  return (
    <div>
      <Spin spinning={scatterLoading}>
        {!scatterLoading && (
          <div
            ref={barChartRef}
            id="bar-chart-container"
            style={{ visibility: isCapturing ? "visible" : "visible" }}
          >
            <Bar data={barData} options={barOptions} />
          </div>
        )}
      </Spin>

      <div>
        <Modal
          width={1093}
          className="line-chart-modal"
          open={isModalVisible}
          onCancel={handleModalClose}
          footer={[
            <Button
              className="cancel-button"
              key="cancel"
              onClick={handleModalClose}
            >
              Cancel
            </Button>,
          ]}
        >
          <div className="modal-header">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                width: "7%",
                justifyContent: "center",
              }}
            >
              <div style={{ position: "relative", display: "inline-block" }}>
                <img
                  src={pdfSvg}
                  alt="Download PDF"
                  style={{
                    width: 40,
                    opacity: apiResponseReceived ? 1 : 0.5,
                    filter: apiResponseReceived ? "none" : "grayscale(100%)",
                  }}
                />
                {!apiResponseReceived && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      cursor: "not-allowed",
                      backgroundColor: "rgba(255, 255, 255, 0.5)",
                    }}
                    title="Please wait for data to load"
                  />
                )}
                {apiResponseReceived && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      cursor: "pointer",
                    }}
                    onClick={handlePdfDownloadProcess}
                  />
                )}
              </div>
            </div>
            <div className="date-indicator">
              <div className="date-select-row">
                <div className="indicator-label">
                  <span className="indicator-title-header">
                    {commonStrings.indicator}:
                  </span>{" "}
                  <span className="indicator-number-header">
                    {selectedIndicator}
                  </span>
                </div>
                <div className="date-picker-wrapper">
                  <span className="select-date">
                    {commonStrings.selectDate}
                  </span>
                  <div direction="vertical" size={12}>
                    <RangePicker
                      value={dateRange}
                      onChange={handleDateChange}
                      disabledDate={disabledDates}
                      allowClear
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Spin spinning={lineChartLoading}>
            <div
              ref={lineChartRef}
              id="line-chart-container"
              style={{
                background: "white",
                padding: 20,
                visibility: isCapturing ? "visible" : "visible",
              }}
            >
              {noLineDataFound ? (
                <div>No data available for selected dates</div>
              ) : (
                <Line data={lineChartData} options={lineOptions} />
              )}
            </div>
          </Spin>
        </Modal>
      </div>

      {/* PDF Download Modal */}
      <Modal
        title={null}
        className="activity-details-modal"
        open={isPdfModalVisible}
        onCancel={() => setIsPdfModalVisible(false)}
        footer={null}
        centered
        bodyStyle={{ textAlign: "center", padding: "30px" }}
      >
        {pdfLoading ? (
          <div className="pdf-generation-container">
            <div className="pdf-loader-section">
              <img
                src={dottedLoader}
                alt="Loading..."
                className="pdf-loader-icon"
              />
              <span className="pdf-preparing-text">
                {commonStrings.pdfPreparing}
              </span>
              <span className="pdf-waiting-text">{commonStrings.waiting}</span>
            </div>
          </div>
        ) : pdfDownloadReady ? (
          <div className="pdf-ready-container">
            <div className="pdf-info-section">
              <img src={pdfSvg} alt="PDF Icon" className="pdf-icon" />{" "}
              <span className="pdf-ready-text">PDF is ready to download</span>{" "}
            </div>
          </div>
        ) : null}

        <div style={{ display: "none" }}>
          <PDFDownloadLink
            className="pdf-download-link"
            document={(
              <IndicatorReportPdf
                stage={stage}
                selectedIndicator={selectedIndicator}
                lineChartUrl={capturedImages.lineChart}
                barChartUrl={capturedImages.barChart}
                indicator={selectedIndicator}
                showInitialValueColumn={showInitialValueColumn}
                showPreviousRatingColumn={showPreviousRatingColumn}
                showStatusColumn={showStatusColumn}
                indicatorTable={
                  tableListing
                  && Array.isArray(tableListing.data)
                  && tableListing.data.length > 0
                    ? getMergedDataWithKeysforNew(tableListing)
                    : tableListingold && Array.isArray(tableListingold.data)
                      ? getMergedDataWithKeysforOld(tableListingold)
                      : []
                }
                currentschoolLogo={currentschoolLogo}
              />
            )}
            fileName={`indicator_${selectedIndicator}_report.pdf`}
          />
        </div>
        <div className="pdf-action-buttons">
          <Button
            className="cancel-button"
            key="cancel"
            onClick={() => setIsPdfModalVisible(false)}
          >
            Cancel
          </Button>

          <Button
            className="download-button"
            key="download"
            type="primary"
            disabled={!pdfDownloadReady}
            onClick={() => {
              document.querySelector(".pdf-download-link").click()
              setIsPdfModalVisible(false)
            }}
          >
            {pdfLoading ? "Preparing..." : "Download"}
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export default IndicatorChart
